import type {
  ResponseData,
  SubQuestionResponseEntry,
  SubResponse,
} from '@amzn/gbsa-helios-typescript-client';
import type { PreviousResponseForSave } from './deriveTopLevelFieldsForSave';

/**
 * NaReason override: undefined = preserve the persisted label,
 * null = clear both fields, {reasonId,label} = replace both.
 */
export type NaReasonOverride = {
  readonly reasonId: string;
  readonly label: string;
} | null;

/**
 * Per-field overrides, all tri-state (absent = preserve). Clear
 * signals differ by shape: commentOverride '' (after trim),
 * naReasonOverride null, mediaIdsOverride empty list. `| undefined`
 * lets callers pass through optional values without conditional
 * spreads (exactOptionalPropertyTypes).
 */
export interface BuildPreservedResponseDataOptions {
  readonly commentOverride?: string | undefined;
  readonly naReasonOverride?: NaReasonOverride | undefined;
  readonly mediaIdsOverride?: readonly string[] | undefined;
}

/**
 * Build the parent `response` block from the cached response with
 * optional per-field overrides. Returns undefined when empty — an
 * empty `response: {}` is ambiguous with "clear everything" on the
 * wire. Shared by every UpdateAuditQuestionResponse builder. Note:
 * the read shape carries only the naReason label; the id is only
 * available via naReasonOverride.
 *
 * Accepts {@link PreviousResponseForSave} (a
 * `Omit<QuestionResponseDetails, 'responseResult'>`). This narrowing
 * keeps the untrustworthy top-level aggregate off the read surface
 * of every not-an-answer builder — those builders receive
 * `PreviousResponseForSave` and get their top-level answer fields
 * via {@link deriveTopLevelFieldsForSave} instead. Callers with a
 * full `QuestionResponseDetails` can still pass it in (subtype
 * assignability); nothing in this helper reads `responseResult`.
 */
export function buildPreservedResponseData(
  previous: PreviousResponseForSave | undefined,
  options: BuildPreservedResponseDataOptions = {},
): ResponseData | undefined {
  const effectiveComment = resolveEffectiveComment(
    options.commentOverride,
    previous?.responseComment,
  );
  const effectiveReason = resolveEffectiveNaReason(
    options.naReasonOverride,
    previous?.naReason,
  );
  const effectiveMediaIds = resolveEffectiveMediaIds(
    options.mediaIdsOverride,
    previous?.mediaIds,
  );
  const hasComment = effectiveComment !== undefined && effectiveComment !== '';
  const hasReasonLabel =
    effectiveReason.label !== undefined && effectiveReason.label !== '';
  const hasReasonId = effectiveReason.reasonId !== undefined;
  const hasMediaIds = effectiveMediaIds.length > 0;
  const hasTaskIds = !!previous?.taskIds && previous.taskIds.length > 0;

  if (!hasComment && !hasReasonLabel && !hasReasonId && !hasMediaIds && !hasTaskIds) {
    return undefined;
  }

  const response: ResponseData = {};
  if (hasComment) response.comment = effectiveComment;
  if (hasReasonLabel) response.naReason = effectiveReason.label;
  if (hasReasonId) response.naReasonId = effectiveReason.reasonId;
  if (hasMediaIds) response.mediaIds = [...effectiveMediaIds];
  if (hasTaskIds) response.taskIds = previous?.taskIds;
  return response;
}

/**
 * Options for {@link projectSubResponsesForPreservation}.
 *
 * Maps are keyed on `subQuestionId`. Missing keys preserve the
 * previously-saved value for that field on that sub; present keys
 * follow the same tri-state as the parent-level overrides.
 */
export interface ProjectSubResponsesOptions {
  readonly commentOverrides?: Readonly<Record<string, string>>;
  readonly naReasonOverrides?: Readonly<Record<string, NaReasonOverride>>;
  /**
   * Per-sub mediaIds replacement, keyed by subQuestionId. Missing
   * key = preserve; present key = replace with exactly the given
   * list (empty list = clear — field omitted on the wire).
   */
  readonly mediaIdsOverrides?: Readonly<Record<string, readonly string[]>>;
}

/**
 * Project read-side sub entries onto write-side `subResponses`,
 * carrying EVERY field forward (full-PATCH: anything not re-asserted
 * per sub is cleared server-side — the pre-CR-V6 latent-drop bug).
 * Answer builders merge their target entry over this projection.
 */
export function projectSubResponsesForPreservation(
  entries: readonly SubQuestionResponseEntry[] | undefined,
  options: ProjectSubResponsesOptions = {},
): SubResponse[] {
  if (!entries || entries.length === 0) return [];
  const { commentOverrides, naReasonOverrides, mediaIdsOverrides } = options;
  return entries.map((entry) =>
    projectSingleEntry(entry, commentOverrides, naReasonOverrides, mediaIdsOverrides),
  );
}

/**
 * Per-entry projection — split from the map body so the outer
 * function stays under the sonarjs cognitive-complexity budget of
 * 15. Each field's copy-forward decision is a small independent
 * predicate; grouping them here reads sequentially and lets the
 * outer helper stay a one-liner map.
 *
 * Server-computed field preservation (P514589524): The backend
 * wholesale-replaces the sub-response list with the client payload.
 * Fields like maxScore, score, respondedBy, and respondedAt must be
 * echoed back or they are permanently lost. We now carry these
 * fields forward when present.
 */
function projectSingleEntry(
  entry: SubQuestionResponseEntry,
  commentOverrides: Readonly<Record<string, string>> | undefined,
  naReasonOverrides: Readonly<Record<string, NaReasonOverride>> | undefined,
  mediaIdsOverrides: Readonly<Record<string, readonly string[]>> | undefined,
): SubResponse {
  const previous = entry.response;
  const projected: SubResponse = { subQuestionId: entry.subQuestionId };

  if (previous?.responseResult !== undefined) {
    projected.result = previous.responseResult;
  }

  const effectiveComment = resolveEffectiveComment(
    readCommentOverride(commentOverrides, entry.subQuestionId),
    previous?.responseComment,
  );
  if (effectiveComment !== undefined && effectiveComment !== '') {
    projected.comment = effectiveComment;
  }

  const effectiveReason = resolveEffectiveNaReason(
    readNaReasonOverride(naReasonOverrides, entry.subQuestionId),
    previous?.naReason,
  );
  if (effectiveReason.label !== undefined && effectiveReason.label !== '') {
    projected.naReason = effectiveReason.label;
  }
  if (effectiveReason.reasonId !== undefined) {
    projected.naReasonId = effectiveReason.reasonId;
  }

  const effectiveMediaIds = resolveEffectiveMediaIds(
    readMediaIdsOverride(mediaIdsOverrides, entry.subQuestionId),
    previous?.mediaIds,
  );
  if (effectiveMediaIds.length > 0) {
    projected.mediaIds = [...effectiveMediaIds];
  }
  if (previous?.taskIds && previous.taskIds.length > 0) {
    projected.taskIds = previous.taskIds;
  }

  // ERT: carry the data-collection type + value forward so a
  // Pass/Fail (or any) save on a SIBLING sub doesn't wipe this
  // sub's responseValue on the full-PATCH round-trip (the CR-V6
  // latent-drop class). These fields live top-level on the entry,
  // not inside `response`. Additive — legacy scored subs carry
  // neither field, so this is a no-op for existing Pass/Fail
  // audits. An empty-string value is treated as cleared (omitted).
  if (entry.responseType !== undefined) {
    projected.responseType = entry.responseType;
  }
  if (entry.responseValue !== undefined && entry.responseValue !== '') {
    projected.responseValue = entry.responseValue;
  }

  // Server-computed scoring fields: delegated to keep this function
  // under the sonarjs cognitive-complexity budget.
  projectServerComputedFields(entry, projected);

  return projected;
}

/**
 * Server-computed scoring fields (P514589524 fix): carry forward `maxScore`,
 * the static definition value the server needs echoed back. The server's
 * update mapper wholesale-replaces the sub-response list with the client
 * payload; `maxScore` must be echoed or scoring breaks.
 *
 * Critical field: `maxScore`. When omitted, the server's scoring engine
 * awards 0 points to passing sub-questions, destroying partial credit
 * for any FAIL multi-part question. This was the root cause of P514589524.
 *
 * The SDK's read type (`QuestionResponseDetails`) doesn't declare `maxScore`,
 * but the server sends it on the wire and expects it back. We access it via
 * a type assertion on the entry-level object (where the server actually puts
 * it — the SDK type gap affects the `response` wrapper but the data is there).
 *
 * **NOT carried forward**: `score`, `respondedBy`, `respondedAt`. These are
 * per-answer authorship fields the server recomputes on each write. Carrying
 * them forward would send stale values when a sub is re-answered (e.g.,
 * PASS→FAIL would ship the old PASS score with the FAIL result, and keep
 * the old responder/timestamp instead of the current auditor).
 *
 * Extracted to keep `projectSingleEntry` under the sonarjs cognitive-
 * complexity budget of 15.
 */
function projectServerComputedFields(
  entry: SubQuestionResponseEntry,
  projected: SubResponse,
): void {
  // maxScore: critical for partial scoring. The SDK read type doesn't declare it,
  // but the server sends it on the wire. Access via type assertion on the entry
  // since it may be at entry level or in response depending on SDK version.
  const maxScore =
    (entry as unknown as { maxScore?: number }).maxScore ??
    (entry.response as unknown as { maxScore?: number })?.maxScore;
  if (maxScore !== undefined) {
    projected.maxScore = maxScore;
  }
  // Note: score, respondedBy, respondedAt are intentionally NOT carried forward.
  // The server recomputes these on each write; echoing stale values would
  // misattribute authorship and send outdated scores on re-answers.
}

/**
 * Tri-state comment resolver: undefined = omit, '' = clear (trimmed),
 * non-blank = set.
 */
function resolveEffectiveComment(
  override: string | undefined,
  previousComment: string | undefined,
): string | undefined {
  if (override === undefined) return previousComment;
  return override.trim();
}

/**
 * Tri-state naReason resolver — shared between parent-block and
 * per-sub-entry projections so the preserve / clear / replace
 * branching lives in one place. Returns the effective label + id
 * pair the emitter should stamp on the wire:
 *   - PRESERVE (override undefined) → label from previous, no id
 *   - CLEAR (override null)         → both undefined
 *   - REPLACE ({reasonId, label})   → both from override
 */
function resolveEffectiveNaReason(
  override: NaReasonOverride | undefined,
  previousLabel: string | undefined,
): { readonly label: string | undefined; readonly reasonId: string | undefined } {
  if (override === undefined) {
    return { label: previousLabel, reasonId: undefined };
  }
  if (override === null) {
    return { label: undefined, reasonId: undefined };
  }
  return { label: override.label, reasonId: override.reasonId };
}

/**
 * Tri-state mediaIds resolver — shared between the parent block
 * and per-sub-entry projections. Returns the effective list the
 * emitter should stamp on the wire:
 *   - PRESERVE (override undefined) → previous ids (or empty)
 *   - CLEAR / REPLACE (override present) → exactly the override;
 *     an empty override list yields an empty result, which the
 *     callers translate to "omit the field" (full-PATCH unset).
 */
function resolveEffectiveMediaIds(
  override: readonly string[] | undefined,
  previousIds: readonly string[] | undefined,
): readonly string[] {
  if (override !== undefined) return override;
  return previousIds ?? [];
}

/**
 * Safe reader for the mediaIds overrides map — same rationale as
 * {@link readCommentOverride}. Returns `undefined` for missing
 * keys (preserve) or the replacement list.
 */
function readMediaIdsOverride(
  overrides: Readonly<Record<string, readonly string[]>> | undefined,
  key: string,
): readonly string[] | undefined {
  if (!overrides) return undefined;
  // eslint-disable-next-line security/detect-object-injection
  return overrides[key];
}

/**
 * Safe reader for the comment overrides map. Isolates the
 * dynamic-key access so the security/detect-object-injection lint
 * disable-comment lives in one place instead of scattering across
 * every entry projection.
 */
function readCommentOverride(
  overrides: Readonly<Record<string, string>> | undefined,
  key: string,
): string | undefined {
  if (!overrides) return undefined;
  // eslint-disable-next-line security/detect-object-injection
  return overrides[key];
}

/**
 * Safe reader for the naReason overrides map — same rationale as
 * {@link readCommentOverride}. Returns `undefined` for missing
 * keys (preserve), `null` for cleared keys, or the override
 * object for replaced keys.
 */
function readNaReasonOverride(
  overrides: Readonly<Record<string, NaReasonOverride>> | undefined,
  key: string,
): NaReasonOverride | undefined {
  if (!overrides) return undefined;
  // eslint-disable-next-line security/detect-object-injection
  return overrides[key];
}
